import s from './index.module.css';
import MainChatPage from "@common/Chat/MainChatPage/MainChatPage";
import CreateChat from "@common/Chat/CreateChat/CreateChat";
import ChatView from "@common/Chat/components/ChatView/ChatView";
import {useEffect, useState} from "react";
import DefaultView from "@common/Chat/DefaultView/DefaultView";
import InChatView from "@common/Chat/InChatView/InChatView";
import ChatInfo from "@common/Chat/ChatInfo/ChatInfo";
import {chatApi} from "@/api";
import {Resizable} from 're-resizable';
import {checkHeight, checkMinWidth, checkWidth} from "@common/Chat/helpers";
import classNames from "classnames";
import {useAddAlert} from "@/hooks/useAddAlert";
import websocket from '@/utils/websocket';
import {useChatContext} from './ChatContext';
import {useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import useIsTechSupport from '@common/Chat/SupportChatButton/useIsTechSupport';
import { getNotificationPlainText } from './helpers/mentions';

export const pages = {
    default: DefaultView,
    main: MainChatPage,
    chat: ChatView,
    create: CreateChat,
    inChat: InChatView,
    info: ChatInfo,
}

const Component = ({
                       page,
                       setPage,
                       chat = null,
                       typeAction = 'create',
                       setTypeAction,
                       unReadChats,
                       lessonUnReadChats = [],
                       activeUsers,
                       height,
                       params,
                       getActiveUsers,
                       isFullPage = false,
                       belowTabsSlot = null,
                       integrationListSlot = null,
                       isIntegrationListActive = false,
                       showChatListModeTabs = false,
                       chatListMode = 'internal',
                       onChatListModeChange = undefined,
                       integrationUnreadTotal = 0,
                       integrationHasConnectedProviders = true,
                       onPlatformTabSelect = undefined,
                   }) => {
    const Component = pages[page]
    return (
        <>
            {Component && <Component
                setPage={setPage}
                height={height}
                chat={chat}
                page={page}
                setTypeAction={setTypeAction}
                typeAction={typeAction}
                unReadChats={unReadChats}
                lessonUnReadChats={lessonUnReadChats}
                activeUsers={activeUsers}
                params={params}
                getActiveUsers={getActiveUsers}
                isFullPage={isFullPage}
                belowTabsSlot={belowTabsSlot}
                integrationListSlot={integrationListSlot}
                isIntegrationListActive={isIntegrationListActive}
                showChatListModeTabs={showChatListModeTabs}
                chatListMode={chatListMode}
                onChatListModeChange={onChatListModeChange}
                integrationUnreadTotal={integrationUnreadTotal}
                integrationHasConnectedProviders={integrationHasConnectedProviders}
                onPlatformTabSelect={onPlatformTabSelect}
            />}
        </>
    )
}

export const Chat = ({
    chatState,
    chatResize = true,
    className,
    hideLauncher = false,
    belowTabsSlot = null,
    integrationListSlot = null,
    isIntegrationListActive = false,
    showChatListModeTabs = false,
    chatListMode = 'internal',
    onChatListModeChange = undefined,
    integrationUnreadTotal = 0,
    integrationHasConnectedProviders = true,
    onPlatformTabSelect = undefined,
}) => {
    const chatContext = useChatContext()
    const history = useHistory()
    const {isTechSupport} = useIsTechSupport()
    const isChatEnabled = useSelector(store => store.settings.isChatEnabled);
    const chatBackgroundColor = useSelector(store => store.settings.chatBackgroundColor);
    const [updateComponent, setUpdateComponent] = useState(false)
    useEffect(() => {

        const onScroll = (event) => {
            setUpdateComponent(!updateComponent)
        }

        window.addEventListener('resize', onScroll);

        return () => {
            window.removeEventListener('resize', onScroll);
        }

    }, [])

    const showAlert = useAddAlert()

    const {page, chat, typeAction, changePage, params} = chatState;
    const screenWidth = window.innerWidth;
    const widthChatFromLS = JSON.parse(localStorage.getItem('widthChat'));
    const [unReadChats, setUnReadChats] = useState([])
    // Чаты уроков намеренно не входят в общий счётчик непрочитанного, но бейдж
    // и пункт «Отметить прочитанным» в разделе «Чаты уроков» им нужен.
    const [lessonUnReadChats, setLessonUnReadChats] = useState([])
    const [activeUsers, setActiveUsers] = useState([])

    const minWidth = checkMinWidth(page, isChatEnabled == '1' ? unReadChats : [])
    const maxWidthChat = checkWidth(page, isChatEnabled == '1' ? unReadChats : [])
    const heightChat = checkHeight(page)
    const isDefaultPage = page === 'default'

    const [width, setWidth] = useState(widthChatFromLS ? widthChatFromLS : minWidth)
    const effectiveWidth = isDefaultPage ? minWidth : Math.max(width, minWidth)

    useEffect(() => {
        localStorage.setItem('widthChat', JSON.stringify(width))
    }, [width])

    const saveWidthSettings = (width) => {
        setWidth(width)
        window.dispatchEvent(new Event('platform-chat-size-change'))
    }

    // Функция для подсчета непрочитанных сообщений поддержки
    const updateSupportUnreadCount = (unReadChats) => {
        if (!chatContext) return

        const supportUnreadCount = unReadChats?.filter(chat => chat.type === 'support')?.reduce((total, chat) => total + chat.count, 0)


        chatContext.setUnreadSupportMessagesCount(Number(supportUnreadCount ?? 0))
    }

    // Обновляем currentOpenChatId при изменении страницы или чата
    useEffect(() => {
        if (chatContext) {
            if (page === 'inChat' && chat) {
                chatContext.setCurrentOpenChatId(chat);
                console.log('Setting current open chat ID to:', chat);
            } else {
                chatContext.setCurrentOpenChatId(null);
                console.log('Clearing current open chat ID');
            }
        }
    }, [page, chat, chatContext]);

    const getActiveUsers = async () => {
        try {
            const res = await chatApi.getActiveUsers()
            if (res?.data) {
                if (res?.data?.noReadMessages) {
                    setUnReadChats(res?.data?.noReadMessages)
                    updateSupportUnreadCount(res?.data?.noReadMessages)
                }

                setLessonUnReadChats(
                    Array.isArray(res?.data?.lessonNoReadMessages) ? res.data.lessonNoReadMessages : []
                )

                if (page === 'default') {
                    res.data.notifications?.map(showNewMessageNotification)
                }
            }
            return res
        } catch (error) {
            console.error('Error fetching active users:', error)
            throw error
        }
    }

    useEffect(() => {
        getActiveUsers();

        const unsubscribeActiveUsers = websocket.subscribe('active_users', (data) => {
            if (data) {
                setActiveUsers(data?.activeUsers);
            }
        });

        // websocket.subscribe('chat_notifications', (data) => {
        //     if (data) {
        //         if(data?.data?.data?.noReadMessages){
        //                 setUnReadChats(data?.data?.data?.noReadMessages ?? []);
        //                 updateSupportUnreadCount(data?.data?.data?.noReadMessages ?? []);

        //                 // if (page === 'default') {
        //                 //     data?.data?.data?.notifications?.forEach(showNewMessageNotification ?? []);
        //                 // }
        //         }
        //     }
        // });
        const getSnapshotListFromPayload = (payload, key) => {
            if (Array.isArray(payload?.data?.data?.[key])) {
                return payload.data.data[key];
            }

            if (Array.isArray(payload?.notification?.data?.data?.[key])) {
                return payload.notification.data.data[key];
            }

            return null;
        };

        const handleChatReadPayload = (payload) => {
            const noReadMessages = getSnapshotListFromPayload(payload, 'noReadMessages');

            if (!noReadMessages) return;

            setUnReadChats(noReadMessages);
            updateSupportUnreadCount(noReadMessages);
            setLessonUnReadChats(getSnapshotListFromPayload(payload, 'lessonNoReadMessages') ?? []);
        };

        const unsubscribeChatRead = websocket.subscribe('chat.read', handleChatReadPayload);
        const unsubscribeNotification = websocket.subscribe('notification', (data) => {
            if (data?.notification?.type !== 'chat.read') return;

            handleChatReadPayload(data);
        });

        return () => {
            unsubscribeActiveUsers?.();
            unsubscribeChatRead?.();
            unsubscribeNotification?.();
        };
    }, []);

    const showNewMessageNotification = (newMessage) => {
        // Обращения техподдержки сотрудник ведёт в Helpdesk, а не в окне чата:
        // иначе по клику открывался старый интерфейс поддержки.
        const openInHelpdesk = newMessage?.type === 'support' && isTechSupport

        showAlert({
            title: newMessage.user,
            text: getNotificationPlainText(newMessage.message || ''),
            files: newMessage.files,
            sound: true,
            onClick: () => {
                if (openInHelpdesk) {
                    history.push('/helpdesk')
                    return
                }
                changePage('inChat', newMessage.chat_id)
            }
        })
    }


    const componentProps = {
        page,
        height: heightChat,
        setPage: changePage,
        chat,
        setTypeAction: chatState.setTypeAction,
        typeAction,
        unReadChats,
        lessonUnReadChats,
        activeUsers,
        params,
        getActiveUsers,
        isFullPage: !chatResize,
        belowTabsSlot,
        integrationListSlot,
        isIntegrationListActive,
        showChatListModeTabs,
        chatListMode,
        onChatListModeChange,
        integrationUnreadTotal,
        integrationHasConnectedProviders,
        onPlatformTabSelect,
    }

    if (chatResize && hideLauncher && isDefaultPage) {
        return null
    }

    if (chatResize) {
        return (
            <Resizable
                style={{backgroundColor: !!chatBackgroundColor?.trim() ? `${chatBackgroundColor}` : 'var(--color-surface)'}}
                className={classNames(s.chats, isDefaultPage && className)}
                size={{ width: effectiveWidth, height: heightChat }}
                minWidth={minWidth}
                maxWidth={maxWidthChat}
                enable={{ bottom: false, top: false, left: true, right: false }}
                minHeight={heightChat}
                maxHeight={heightChat}
                onResizeStop={(e, direction, ref, d) => saveWidthSettings(effectiveWidth + d.width)}
            >
                <Component {...componentProps} />
            </Resizable>
        )
    }

    return <Component {...componentProps} />
}

export default Chat;
